import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";

export const PLATFORM_NEWS_COLUMNS =
  "id, platform, kind, title, summary, what_changed, laeebly_impact, action_required, action_summary, action_detail, action_deadline, source_url, source_label, effective_at, published_at, announcement_id";
const LIMIT = 100;

/**
 * 발행된 플랫폼 소식 + 내 읽음·확인. 마운트 1회, 폴링 없음(소식은 벨과 섞지 않는다).
 * markRead(id)  — 펼침. platform_news_read upsert(read_at)
 * confirm(id)   — 「확인했어요」. confirmed_at + 연결 공지가 있으면 announcement_recipient.confirmed_at (승급 중단)
 * 기획 docs/ops/platform-news-plan.md · 구현 docs/ops/platform-news-implementation-plan.md Task 4
 */
export default function usePlatformNews(email, { enabled = true } = {}) {
  const [rows, setRows] = useState([]);
  const [readMap, setReadMap] = useState({});
  const [loading, setLoading] = useState(true);

  // markRead/confirm 이 같은 id 에 대해 state 반영 전에 연달아 호출돼도(스테일 클로저)
  // 두 번째 호출이 새 네트워크 요청을 만들지 않도록 진행 중인 id 를 별도로 추적한다.
  const pendingRef = useRef(new Set());

  // 실패 롤백 — 이전에 항목이 아예 없었으면(undefined) 빈 값으로 채우지 않고 키 자체를 지운다.
  const restoreEntry = useCallback((id, prevEntry) => {
    setReadMap((prev) => {
      if (prevEntry === undefined) {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: prevEntry };
    });
  }, []);

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    const { data: news, error } = await supabase
      .from("platform_news")
      .select(PLATFORM_NEWS_COLUMNS)
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(LIMIT);
    if (error || !news) {
      setRows([]);
      setLoading(false);
      return;
    }
    const { data: reads } = await supabase
      .from("platform_news_read")
      .select("news_id, read_at, confirmed_at")
      .eq("email", email);
    const map = {};
    for (const r of reads || []) map[r.news_id] = { read_at: r.read_at, confirmed_at: r.confirmed_at };
    setRows(news);
    setReadMap(map);
    setLoading(false);
  }, [email]);

  useEffect(() => {
    if (!enabled || !email) {
      setRows([]);
      setReadMap({});
      setLoading(false);
      return;
    }
    load();
  }, [enabled, email, load]);

  const markRead = useCallback(async (id) => {
    if (!email || readMap[id]?.read_at) return;
    const pendingKey = `read:${id}`;
    if (pendingRef.current.has(pendingKey)) return;
    pendingRef.current.add(pendingKey);
    try {
      const prevEntry = readMap[id];
      const now = new Date().toISOString();
      setReadMap((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), read_at: now } }));
      // supabase-js 는 던지지 않고 { error } 로 돌려준다 — 실패하면 낙관적으로 찍은 값을 되돌린다.
      const { error } = await supabase
        .from("platform_news_read")
        .upsert({ news_id: id, email, read_at: now }, { onConflict: "news_id,email", ignoreDuplicates: true });
      if (error) {
        console.warn("[platformNews] markRead", error);
        restoreEntry(id, prevEntry);
      }
    } finally {
      pendingRef.current.delete(pendingKey);
    }
  }, [email, readMap, restoreEntry]);

  const confirm = useCallback(async (id) => {
    if (!email || readMap[id]?.confirmed_at) return;
    const pendingKey = `confirm:${id}`;
    if (pendingRef.current.has(pendingKey)) return;
    pendingRef.current.add(pendingKey);
    try {
      const prevEntry = readMap[id];
      const now = new Date().toISOString();
      const readAt = readMap[id]?.read_at || now;
      setReadMap((prev) => ({ ...prev, [id]: { read_at: readAt, confirmed_at: now } }));
      // read_at 은 보내지 않는다 — DB 기본값 now() 가 insert 시 채우고, update 시 서버의 기존 read_at 이 유지된다.
      const { error } = await supabase
        .from("platform_news_read")
        .upsert({ news_id: id, email, confirmed_at: now }, { onConflict: "news_id,email" });
      if (error) {
        console.warn("[platformNews] confirm", error);
        restoreEntry(id, prevEntry);
        return;
      }
      const annId = rows.find((r) => r.id === id)?.announcement_id;
      if (annId) {
        const { error: annError } = await supabase
          .from("announcement_recipient")
          .update({ confirmed_at: now })
          .eq("announcement_id", annId)
          .eq("user_email", email)
          .is("confirmed_at", null);
        if (annError) {
          // platform_news_read 는 이미 정상 반영됐으므로 롤백하지 않고 경고만 남긴다.
          console.warn("[platformNews] confirm announcement_recipient", annError);
        }
      }
    } finally {
      pendingRef.current.delete(pendingKey);
    }
  }, [email, readMap, rows, restoreEntry]);

  return { rows, readMap, loading, markRead, confirm, reload: load };
}
